import { CustomEventWithDetail, type Html, html, View } from 'rune-ts';
import klass from './CreatorSearchCard.module.scss';
import type { CreatorSearchData } from '../../../../features/Creator/type';
import { getResizedImageUrl } from '../../../../shared/util/getResizedImageUrl.ts';
import { makeDefaultProfileImg } from '../../../../shared/util/helper.ts';
import { font, staticFont } from '../../../../shared/typography';
import {
  MShopDefaultIcon,
  myFanIconHeart,
} from '../../../../shared/components/atoms/Icon/foundation_icons.ts';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction.ts';
import { find, isUndefined, map, pipe, reject, take, throwIf, toArray } from '@fxts/core';
import { LegacyProductThumbnail } from '../ProductThumbnail/LegacyProductThumbnail.ts';
import { ProductListTypeS } from '../../../../features/ProductList/outbound/share.ts';
import { makeEmptyScreenNavigate, makeUtilScreenNavigate } from '../../../../shared/app/navigate.ts';
import { dataStr } from '../../../../shared/util/dataStr.ts';
import { CreatorUtilS } from '../../../../features/Creator/outbound/share.ts';
import { humanReadableNumberV2 } from '../../../../../../shared/utils/formatter';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';
import { htmlIf } from '@services/MarppleShop/shared/util';

export interface CreatorSearchCardData {
  creator: CreatorSearchData;
  is_mobile: boolean;
  is_lazy?: boolean;
}

class CreatorSearchCardProduct extends LegacyProductThumbnail {
  static wrapWithLink(
    data: ProductListTypeS.ProductThumbnailData,
    option: ProductListTypeS.ProductThumbnailOption,
    domain_name: string,
    product_id: number,
  ) {
    const view = new CreatorSearchCardProduct(data, option);
    const product_detail_url = `/${domain_name}/products/${product_id}`;
    const navigate = makeEmptyScreenNavigate(product_detail_url);

    return html`
      <a
        class="${klass.product_image_link}"
        href="${product_detail_url}"
        data-post-message="${dataStr(navigate)}"
      >
        ${view}
      </a>
    `;
  }
}

class ProposalButtonClickEvent extends CustomEventWithDetail<
  Pick<CreatorSearchCardData['creator'], 'name'>
> {}

class FanButtonClickEvent extends CustomEventWithDetail<
  Pick<CreatorSearchCardData['creator'], 'id' | 'is_liked'>
> {}

export class CreatorSearchCard extends View<CreatorSearchCardData> {
  static MO_DISPLAY_COUNT = 2;
  static PC_DISPLAY_COUNT = 3;

  static ProposalButtonClickEvent = ProposalButtonClickEvent;
  static FanButtonClickEvent = FanButtonClickEvent;

  private readonly shopButtonView: ButtonAction;

  private readonly proposalButtonView: ButtonAction;

  private like_count: number;

  constructor(data: CreatorSearchCardData) {
    super(data);

    const { texts } = this;

    this.shopButtonView = new ButtonAction({
      type: 'white',
      label: texts.shop_button_label,
      size: 'regular',
      href: this.storeUrl,
      navigate: makeUtilScreenNavigate(this.storeUrl),
    });
    this.proposalButtonView = new ButtonAction({
      type: 'white',
      label: texts.proposal_button_label,
      size: 'regular',
      fit: !this.isMobile,
      klass: klass.collaboration_button,
    });

    this.like_count = data.creator.like_count || 0;
  }

  override template() {
    return this.isMobile ? this.makeMoHtml() : this.makePcHtml();
  }

  private makePcHtml() {
    const { creator } = this.data;
    const { texts } = this;

    return html`<div
      class="${klass.creator_search_card}"
      style="background-image: url('${this.makeBgImage()}')"
    >
      <div class="${klass.bg}"></div>

      <div class="${klass.top}">
        <img class="${klass.thumbnail}" src="${this.makeProfileImage()}" alt="" />

        <div class="${klass.profile}">
          <div class="${klass.info}">
            <div class="${klass.name_wrapper}">
              <div class="${klass.creator_name} ${font('16_bold')}">
                <span class="${klass.name}">${html.preventEscape(creator.name || '')} </span>
                <span class="${klass.shop_icon}">${MShopDefaultIcon()}</span>
              </div>
              <div class="${klass.creator_category} ${font('14_medium')}">
                ${creator.seller_type_name || ''}
              </div>
            </div>
            <div class="${klass.fan_icon} ${creator.is_liked ? klass.fan_icon_liked : ''}">
              ${myFanIconHeart({ color: creator.is_liked ? 'blue' : 'gray' })}
            </div>
          </div>
          <div class="${klass.buttons}">${this.shopButtonView} ${this.proposalButtonView}</div>
        </div>
      </div>

      <div class="${klass.bottom}">
        <div class="${klass.statistics}">
          <div class="${klass.statistic}">
            <div class="${klass.field} ${font('12_medium')}">${texts.fan}</div>
            <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
              ${this.makeHumanReadable(this.like_count)}
            </div>
          </div>
          <a
            href="${this.fanLetterUrl}"
            data-post-message="${dataStr(this.makeNavigate(this.fanLetterUrl))}"
            class="${klass.statistic}"
          >
            <div class="${klass.field} ${font('12_medium')}">${texts.fan_letter}</div>
            <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
              ${this.makeHumanReadable(creator.article_count || 0)}
            </div>
          </a>
          ${creator.current_rank
            ? html`
                <div class="${klass.statistic} ${klass.wide}">
                  <div class="${klass.field} ${font('12_medium')}">${texts.ranking}</div>
                  <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
                    <div class="${klass.rank_name}">
                      <div class="${klass.rank_num}">${creator.current_rank}</div>
                      <div class="${font('16_bold')}">${this.formatRank(creator.current_rank)}</div>
                    </div>
                    ${this.makeRankChangeHtml()}
                  </div>
                </div>
              `
            : ''}
        </div>

        <div class="${klass.products}">${this.makeProductThumbnailViews()}</div>
      </div>
    </div> `;
  }

  private makeMoHtml() {
    const { creator } = this.data;
    const { texts } = this;

    return html`<div class="${klass.creator_search_card}">
      <div class="${klass.top}">
        <img
          class="${klass.thumbnail} ${htmlIf('lazy-src', !!this.data.is_lazy)}"
          ${this.data.is_lazy ? 'data-src' : 'src'}="${this.makeProfileImage()}"
          alt=""
        />

        <div class="${klass.profile}">
          <div class="${klass.info}">
            <div class="${klass.creator_name} ${font('16_bold')}">
              <span class="${klass.name}">${html.preventEscape(creator.name || '')} </span>
              <span class="${klass.shop_icon}">${MShopDefaultIcon()}</span>
            </div>
            <div class="${klass.creator_category} ${font('14_medium')}">
              ${creator.seller_type_name || ''}
            </div>
          </div>
        </div>

        <div class="${klass.fan_icon} ${creator.is_liked ? klass.fan_icon_liked : ''}">
          ${myFanIconHeart({ color: creator.is_liked ? 'blue' : 'gray' })}
        </div>
      </div>

      <div class="${klass.bottom}">
        <div class="${klass.statistics}">
          <div class="${klass.statistic}">
            <div class="${klass.field} ${font('12_medium')}">${texts.fan}</div>
            <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
              ${this.makeHumanReadable(this.like_count)}
            </div>
          </div>
          <a
            href="${this.fanLetterUrl}"
            data-post-message="${dataStr(this.makeNavigate(this.fanLetterUrl))}"
            class="${klass.statistic} ${klass.fan_letter}"
          >
            <div class="${klass.field} ${font('12_medium')}">${texts.fan_letter}</div>
            <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
              ${this.makeHumanReadable(creator.article_count || 0)}
            </div>
          </a>
          ${creator.current_rank
            ? html`
                <div class="${klass.statistic} ${klass.wide}">
                  <div class="${klass.field} ${font('12_medium')}">${texts.ranking}</div>
                  <div class="${klass.value} ${staticFont('bebas_24_bold', 'bebas_20_bold')}">
                    <div class="${klass.rank_name}">
                      <div class="${klass.rank_num}">${creator.current_rank}</div>
                      <div class="${font('16_bold')}">${this.formatRank(creator.current_rank)}</div>
                    </div>
                    ${this.makeRankChangeHtml()}
                  </div>
                </div>
              `
            : ''}
        </div>

        <div class="${klass.products}">${this.makeProductThumbnailViews()}</div>
      </div>

      <div class="${klass.buttons}">${this.proposalButtonView} ${this.shopButtonView}</div>
    </div> `;
  }

  private formatRank(rank: number, locale = ET.lang): string {
    switch (locale) {
      case 'kr':
        return `위`;

      case 'en': {
        const suffix = (() => {
          if (rank % 100 >= 11 && rank % 100 <= 13) return 'th';
          switch (rank % 10) {
            case 1:
              return 'st';
            case 2:
              return 'nd';
            case 3:
              return 'rd';
            default:
              return 'th';
          }
        })();
        return `${suffix}`;
      }

      case 'jp':
        return `位`;

      default:
        return `위`;
    }
  }

  protected override onRender() {
    this.delegate('click', `.${klass.collaboration_button}`, () => {
      this.dispatchEvent(CreatorSearchCard.ProposalButtonClickEvent, {
        bubbles: true,
        detail: { name: this.data.creator.name },
      });
    });

    this.delegate('click', `.${klass.fan_icon}`, () => {
      this.dispatchEvent(CreatorSearchCard.FanButtonClickEvent, {
        bubbles: true,
        detail: { id: this.data.creator.id, is_liked: !this.data.creator.is_liked },
      });
    });

    this.addEventListener('click', (e) => {
      // 위에 아무리 stopPropagation 을 해도 이벤트 버블링이 되어버려서 하위 방식으로 대체
      const target = e.target as HTMLElement;
      if (
        target.closest(`.${klass.fan_icon}`) ||
        target.closest(`.${klass.collaboration_button}`) ||
        target.closest(`.${klass.fan_letter}`) ||
        target.closest(`.${klass.product_image_link}`)
      ) {
        return;
      }

      if (MShopUtilF.isApp()) {
        MShopUtilF.postMessage(makeEmptyScreenNavigate(this.storeUrl));
      } else {
        window.location.href = this.storeUrl;
      }
    });
  }

  private makeProfileImage() {
    return getResizedImageUrl({
      url: this.data.creator.thumbnail_img || this.default_image,
      format: 'webp',
      quality: 80,
      width: this.isMobile ? 80 * 2 : 112 * 2,
      bg: '#ffffff',
    });
  }

  private makeBgImage() {
    return getResizedImageUrl({
      url: this.data.creator.thumbnail_img || this.default_image,
      format: 'webp',
      quality: 80,
      width: 437 * 2,
    });
  }

  private makeProductThumbnailViews() {
    const { creator, is_lazy } = this.data;

    return pipe(
      creator.products,
      reject((product) => !product.thumbnails),
      map((product) =>
        CreatorSearchCardProduct.wrapWithLink(
          product,
          {
            resize_width: 48 * 2,
            klass: klass.product_image,
            is_lazy,
          },
          creator.domain_name,
          product.id,
        ),
      ),
      take(this.isMobile ? CreatorSearchCard.MO_DISPLAY_COUNT : CreatorSearchCard.PC_DISPLAY_COUNT),
      toArray,
    );
  }

  private makeRankChangeHtml() {
    const { previous_rank, current_rank, score, id } = this.data.creator;

    if (!current_rank) {
      return '';
    }

    const { state, diff } = CreatorUtilS.getUpDownState({
      previous_rank: previous_rank || null,
      current_rank,
      score: score || 0,
      store_id: id,
    });
    return pipe(
      this.rankChangeStates,
      find((change_state) => change_state.state === state),
      throwIf(isUndefined, () => console.error('올바른 랭킹 데이터가 아닙니다.', this.data)),
      (change_state) => change_state.makeHtml(state, diff),
    );
  }

  public setLikeState(is_liked: boolean) {
    this.data.creator.is_liked = is_liked;
    this.data.is_lazy = false;

    this.like_count += is_liked ? 1 : -1;

    this.redraw();
  }

  private makeNavigate(url: string) {
    return makeEmptyScreenNavigate(url);
  }

  private get rankChangeStates(): {
    state: CreatorUtilS.DiffIconKey;
    makeHtml: (state: CreatorUtilS.DiffIconKey, diff: number) => Html;
  }[] {
    return [
      {
        state: 'new',
        makeHtml(state) {
          return html`
            <div class="${klass.rank} ${klass.new} ${staticFont('unica_12_bold')}">
              ${CreatorUtilS.diffIcons[state]}
            </div>
          `;
        },
      },
      {
        state: 'up',
        makeHtml(state, diff) {
          return html`
            <div class="${klass.rank} ${klass.up} ${staticFont('unica_12_bold')}">
              ${CreatorUtilS.diffIcons[state]} ${Math.abs(diff)}
            </div>
          `;
        },
      },
      {
        state: 'down',
        makeHtml(state, diff) {
          return html`
            <div class="${klass.rank} ${klass.down} ${staticFont('unica_12_bold')}">
              ${CreatorUtilS.diffIcons[state]} ${Math.abs(diff)}
            </div>
          `;
        },
      },
      {
        state: 'zero',
        makeHtml() {
          return html``;
        },
      },
    ];
  }

  private get storeUrl() {
    return `/${ET.lang}/${this.data.creator.domain_name}`;
  }

  private get fanLetterUrl() {
    return `/${ET.lang}/${this.data.creator.domain_name}/fan-letter`;
  }

  private get default_image() {
    return makeDefaultProfileImg(300);
  }

  private get texts() {
    return {
      fan: ET('mps2::creator_search::card::fan'),
      fan_letter: ET('mps2::creator_search::card::fan_letter'),
      ranking: ET('creator_shop::realtime_ranking'),
      shop_button_label: ET('mps2::creator_search::card::shop_button_label'),
      proposal_button_label: ET('mps2::creator_search::card::proposal_button_label'),
    };
  }

  private get isMobile() {
    return this.data.is_mobile;
  }

  private makeHumanReadable(count: number) {
    const { value, unit } = humanReadableNumberV2.to(count, true);
    return html`${value}<span class="${font('16_bold')}">${unit}</span> `;
  }
}
